import { useState, useCallback, useEffect, useRef, MouseEventHandler } from 'react';

export const useDraggable = <T extends HTMLElement>(initialPosition = { x: 100, y: 100 }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState(initialPosition);
    const elementRef = useRef<T>(null);

    // Handle mouse down on drag handle
    const handleMouseDown = useCallback((e: React.MouseEvent<T, MouseEvent>) => {
        if (!elementRef.current) return;

        setIsDragging(true);
        const rect = elementRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });

        // Prevent text selection during drag
        e.preventDefault();
    }, []);

    // Handle mouse move
    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !elementRef.current) return;

            // Get the offset parent (the element that this absolutely positioned element is relative to)
            const offsetParent = elementRef.current.offsetParent || document.body;
            const parentRect = offsetParent.getBoundingClientRect();

            // Calculate position relative to the offset parent
            const newX = e.clientX - parentRect.left - dragOffset.x;
            const newY = e.clientY - parentRect.top - dragOffset.y;

            // Get viewport dimensions for boundary constraints
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const panelWidth = elementRef.current.offsetWidth || 0;
            const panelHeight = elementRef.current.offsetHeight || 0;

            // Calculate the absolute position relative to viewport
            const absoluteX = newX + parentRect.left;
            const absoluteY = newY + parentRect.top;

            // Constrain to viewport bounds (not parent container)
            const constrainedAbsoluteX = Math.max(0, Math.min(absoluteX, viewportWidth - panelWidth));
            const constrainedAbsoluteY = Math.max(0, Math.min(absoluteY, viewportHeight - panelHeight));

            // Convert back to position relative to parent
            const finalX = constrainedAbsoluteX - parentRect.left;
            const finalY = constrainedAbsoluteY - parentRect.top;

            setPosition({
                x: finalX,
                y: finalY,
            });
        },
        [isDragging, dragOffset]
    );

    // Handle mouse up
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Add event listeners for mouse move and up
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return {
        // State
        isDragging,
        position,
        setPosition,

        // Refs
        elementRef,

        // Event handlers
        handleMouseDown,

        // Styles
        dragStyle: {
            transform: `translate(${position.x}px, ${position.y}px)`,
            cursor: isDragging ? 'grabbing' : 'default',
        },
        dragHandleStyle: {
            cursor: 'grab',
        },
    };
};
